import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency, formatDateTime, formatNumber } from "@/lib/format";
import { allocationConfidence, formatConfidence, methodBadge, verdictBadge } from "@/lib/matching";

type Allocation = components["schemas"]["MatchAllocationAdminView"];

interface BillAllocationsTableProps {
  billId: string;
}

// One row per bill line, not per allocation: a line matched to both a PO and a DN has two allocations.
interface LineGroup {
  billLineId: string;
  description: string;
  currency: string;
  allocations: Allocation[];
  net: number;
}

function groupByLine(allocations: Allocation[]): LineGroup[] {
  const groups = new Map<string, LineGroup>();

  for (const a of allocations) {
    let group = groups.get(a.billLineId);
    if (!group) {
      group = {
        billLineId: a.billLineId,
        description: a.billLineDescription,
        currency: a.currency,
        allocations: [],
        net: 0,
      };
      groups.set(a.billLineId, group);
    }
    group.allocations.push(a);
  }

  for (const group of groups.values()) {
    // The PO (price) leg owns the net; a DN allocation is receipt-only, so adding it would double-count.
    // The DN owns the net only when the line has no PO leg. Mirrors BuildLineView on the server.
    const poLinked = group.allocations.filter((a) => a.purchaseOrderId);
    const owning = poLinked.length > 0 ? poLinked : group.allocations;
    group.net = owning.reduce((sum, a) => sum + Number(a.matchedAmount ?? 0), 0);

    group.allocations.sort((a, b) => {
      const aPo = a.purchaseOrderId ? 0 : 1;
      const bPo = b.purchaseOrderId ? 0 : 1;
      return aPo - bPo;
    });
  }

  return [...groups.values()];
}

function CounterpartRow({ allocation }: { allocation: Allocation }) {
  const badge = methodBadge(allocation);
  const confidence = formatConfidence(allocationConfidence(allocation));
  // Only set on reviewer-made matches. Reason only: engineTopConfidence includes the manual-link boost,
  // so it isn't the score the verdict was decided on.
  const verdict = verdictBadge(allocation.engineVerdict);
  const isPo = !!allocation.purchaseOrderId;
  const number = isPo ? allocation.poNumber : allocation.dnNumber;
  const lineDescription = isPo ? allocation.poLineDescription : allocation.dnLineDescription;
  const href = isPo
    ? `/purchase-orders/${allocation.purchaseOrderId}`
    : `/delivery-notes/${allocation.deliveryNoteId}`;

  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 py-1">
      <span className="w-6 shrink-0 text-[10px] font-semibold uppercase text-muted-foreground">
        {isPo ? "PO" : "DN"}
      </span>
      {number ? (
        <Link to={href} className="font-mono text-sm hover:underline">
          {number}
        </Link>
      ) : (
        <span className="text-muted-foreground text-sm">unknown</span>
      )}
      {lineDescription && <span className="text-muted-foreground text-sm">{lineDescription}</span>}
      <span className="text-muted-foreground text-xs tabular-nums">
        {formatNumber(allocation.matchedQuantity)}
      </span>
      <Badge variant={badge.variant} className="text-[10px]">
        {badge.label}
      </Badge>
      {confidence && (
        <span
          className="text-muted-foreground text-xs tabular-nums"
          title="How confident the match is. An exact SKU match is 100% by definition; a similarity match carries the score recorded when it was made."
        >
          {confidence}
        </span>
      )}
      {verdict && (
        <Badge variant={verdict.variant} className="text-[10px]" title={verdict.hint}>
          {verdict.label}
        </Badge>
      )}
      <span className="text-muted-foreground text-xs">{formatDateTime(allocation.createdAt)}</span>
    </div>
  );
}

export function BillAllocationsTable({ billId }: BillAllocationsTableProps) {
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET("/admin/bills/{billId}/allocations", { params: { path: { billId } } })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError) {
          setError("Failed to load allocations.");
        } else {
          setAllocations(data ?? []);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [billId]);

  const lines = useMemo(() => groupByLine(allocations), [allocations]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (error) {
    return <p className="text-destructive text-sm">{error}</p>;
  }
  if (lines.length === 0) {
    return <p className="text-muted-foreground text-sm">No allocations — this bill has no matched lines yet.</p>;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/3">Bill line</TableHead>
            <TableHead>Matched to</TableHead>
            <TableHead className="text-right">Net</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.billLineId} id={`allocation-line-${line.billLineId}`}>
              <TableCell className="align-top font-medium">
                {line.description}
                <span className="text-muted-foreground mt-0.5 block text-xs">
                  {line.allocations.length} counterpart{line.allocations.length === 1 ? "" : "s"}
                </span>
              </TableCell>
              <TableCell className="align-top">
                <div className="divide-border/60 divide-y">
                  {line.allocations.map((a) => (
                    <CounterpartRow key={a.id} allocation={a} />
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right align-top tabular-nums">
                {formatCurrency(line.net, line.currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
